import { useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import Group from 'components/utils/Group'
import WalledContent from 'components/Common/WalledContent'
import OfferTileLoggedInButtons from 'components/OfferList/OfferListTiles/Support/OfferTileLoggedInButtons'

import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useOfferRatePrice } from 'hooks/Offers/useOfferPriceNew'
import useBedbankSelectedDatesRates, { canUseBedbankSelectedDates } from 'hooks/useBedbankSelectedDatesRates'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getCanViewLuxPlusBenefits, getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import useOfferTilePriceBlockExperiment from 'hooks/Experiments/useOfferTilePriceBlockExperiment'
import OfferTilePriceBlockCaption from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockCaption'

interface MappedProps {
  canViewLuxPlusBenefits: boolean
  luxPlusLPPEnabled: boolean
}
interface Props extends MappedProps {
  offer: App.BedbankOffer | App.BedbankOfferSummary | App.BedbankOfferWithRate
  /**
   * @default start
   */
  horizontalAlign?: 'start' | 'center'
  loggedInButtonsAlign?: 'none' | 'center' | 'left' | 'right'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filters?: App.OfferListFilters
  wall?: React.ReactNode
}

function BedbankOfferPriceDetails(props: Props) {
  const {
    offer,
    loggedInButtonsAlign = 'none',
    horizontalAlign = 'start',
    selectedDatesAndRoomsFilter,
    filters,
    canViewLuxPlusBenefits,
    luxPlusLPPEnabled,
    wall: wallOverride,
  } = props
  const intl = useIntl()
  const selectedDuration = canUseBedbankSelectedDates(selectedDatesAndRoomsFilter) ? moment(selectedDatesAndRoomsFilter?.checkOut).diff(selectedDatesAndRoomsFilter?.checkIn, 'days') : undefined

  const hotelNights = selectedDuration ?? offer.sell?.los ?? 0
  const durationText = intl.formatMessage(
    {
      id: 'KaWqYun4zcCJhipn8iNG',
      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
      description: 'Duration in nights followed by "from" for bedbank offer card price caption',
    },
    { count: hotelNights },
  )

  const [{ hotelOnlyRates }, fetching] = useBedbankSelectedDatesRates(offer.id, selectedDatesAndRoomsFilter)

  const { default: pricing, member: memberPricing } = useOfferRatePrice(offer, hotelOnlyRates[0])
  const displayDiscountPercent = luxPlusLPPEnabled && canViewLuxPlusBenefits && (memberPricing?.discountPercent ?? 0) > 0 ? memberPricing?.discountPercent : pricing?.discountPercent

  const offerLocations: Array<string> = useMemo(() => {
    const locations = [
      offer.property?.address.city,
      offer.property?.address.stateProvinceName || '',
      offer.property?.address.countryName]
      .filter(Boolean)

    return locations
  }, [offer])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (!fetching) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        offerId: offer.id,
        price: pricing?.price,
        roomRateId: undefined,
        memberPrice: memberPricing?.price,
        duration: hotelNights,
        availability: pricing?.price !== undefined,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [fetching])

  const {
    priceInPoints,
    memberPriceInPoints,
    pointsToBurn,
    pointsOnly,
    hideZeroCash,
    shouldShowBurn,
  } = useSearchOfferBurnPricing({
    price: pricing?.price ?? 0,
    memberPrice: memberPricing?.price,
    loyaltyProductType: getLoyaltyProductType(offer),
    mx: hotelOnlyRates[0]?.mx,
    viewPriceInPoints: !!filters?.viewPriceInPoints,
    disabled: !isDisplayablePrice(pricing?.price),
  })
  // This component explicitly selects hotelOnlyRates (or the hotel-only sell estimate).
  const priceBlockVariation = useOfferTilePriceBlockExperiment(offer, true, isDisplayablePrice(pricing?.price) && !shouldShowBurn && !filters?.viewPriceInPoints)

  if (!isDisplayablePrice(pricing?.price)) return null

  const wall = wallOverride ?? (
    <OfferTileLoggedInButtons
      align={loggedInButtonsAlign}
      fullWidth={false}
      mdFullWidth={horizontalAlign === 'center'}
    />
  )
  return <Group
    desktopHorizontalAlign={horizontalAlign}
    direction="vertical"
    gap={8}
  >
    <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing} wall={wall}>
      <Group desktopHorizontalAlign={horizontalAlign} direction="vertical">
        <LuxPlusOfferVoucherCallout
          offer={offer}
          mode="search"
          align="start"
        />

        <OfferTilePriceBlockCaption
          priceBlockExperimentVariation={priceBlockVariation}
          duration={hotelNights}
          saleUnit={offer.saleUnit}
          unitCount={hotelOnlyRates[0] ? selectedDatesAndRoomsFilter?.rooms?.length : 1}
        >{durationText}
        </OfferTilePriceBlockCaption>
        {shouldShowBurn && <LoyaltyBurnPriceDisplay
          totalPrice={pricing.price}
          pointsToBurn={pointsToBurn}
          priceInPoints={priceInPoints}
          memberPrice={memberPricing?.price}
          memberPriceInPoints={memberPriceInPoints}
          size="L"
          saleUnit={pricing.saleUnit}
          pointsOnly={pointsOnly}
          hideZeroCash={hideZeroCash}
        />}
        {!shouldShowBurn && <LuxPlusPriceStack
          presentation={priceBlockVariation ? 'price-block-experiment' : undefined}
          testid="main-offer-pricing"
          size="L"
          price={pricing.price}
          memberPrice={memberPricing?.price}
          saleUnit={pricing.saleUnit}
        />}
        {(displayDiscountPercent ?? 0) > 0 && !priceBlockVariation && <PriceRowValueDiscountWithCaption
          size="L"
          originalValue={Math.ceil(pricing.value)}
          discountPercentage={displayDiscountPercent! >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? displayDiscountPercent : undefined}
        />}
      </Group>
      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerLocations={offerLocations}
        offerId={offer.id}
      />
    </WalledContent>
  </Group>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(state),
})

export default connect(mapStateToProps)(BedbankOfferPriceDetails)
